/**
 * Size Indicator (PDP)
 *
 * Mirrors the fit-slider position from the Reviews.io aggregate widget
 * lower on the PDP into a compact track in the buy-box. We can no longer
 * derive the position from the Reviews.io API (their average_rating
 * payload is a centred-range value with no documented formula), so we
 * read the rendered `left: X%` from the widget DOM instead.
 *
 * Why polling instead of MutationObserver: Reviews.io sets the slider
 * button position via inline `style.left` assignment after the button is
 * already in the DOM. A childList/subtree observer doesn't fire on that
 * attribute change, so we poll the rendered value directly.
 *
 * Three exit conditions:
 *   1) success — aggregate slider [1] has a `left` value → copy it.
 *   2) no fit data — the widget footer has been present for FOOTER_GRACE_MS
 *      and slider [1] still hasn't appeared with a position → remove.
 *   3) timeout (10s) — Reviews.io never produced a position → remove.
 *
 * The footer grace matters: Reviews.io renders its footer before it
 * positions the fit slider, so "footer present + no slider" is true for a
 * moment even on products that do have fit data. Removing immediately on
 * that signal drops the container just before the position lands.
 */

(function () {
  const TOTAL_TIMEOUT_MS = 10000;
  const POLL_INTERVAL_MS = 150;
  const FOOTER_GRACE_MS = 3000;

  function initSizeIndicator(container) {
    const startedAt = Date.now();
    let footerFirstSeenAt = null;

    function poll() {
      // Reviews.io renders two aggregate sliders in .header__group--center
      // in a fixed order: [0] Tragekomfort, [1] Passform. Per-review sliders
      // carry --sm and live elsewhere; they are excluded.
      const aggregateSliders = document.querySelectorAll(
        '.header__group--center .R-SliderIndicator:not(.R-SliderIndicator--sm)'
      );
      const passformSlider = aggregateSliders[1];
      const button = passformSlider && passformSlider.querySelector('.R-SliderIndicator__button');
      const left = button && button.style.left;

      // (1) Success — copy the position as soon as it lands.
      if (left) {
        applyPosition(container, left);
        return;
      }

      // (2) No fit data — give up only after the footer has been present long
      // enough that a slider would have appeared if the product had one.
      if (document.querySelector('.reviews-io-polaris-widget .ElementsWidget__footer')) {
        if (footerFirstSeenAt === null) footerFirstSeenAt = Date.now();
        if (Date.now() - footerFirstSeenAt > FOOTER_GRACE_MS) {
          container.remove();
          return;
        }
      }

      // (3) Backstop
      if (Date.now() - startedAt > TOTAL_TIMEOUT_MS) {
        container.remove();
        return;
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    }

    poll();
  }

  function applyPosition(container, left) {
    const thumb = container.querySelector('[data-size-indicator-thumb]');
    if (!thumb) return;
    thumb.style.left = left;
    thumb.style.opacity = '1';
    const percent = parseFloat(left);
    if (Number.isFinite(percent)) {
      container.setAttribute('aria-valuenow', String(Math.round(percent)));
    }
    container.classList.remove('size-indicator--loading');
  }

  function run() {
    document.querySelectorAll('.size-indicator').forEach(initSizeIndicator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
